import React, { useState, useMemo, useRef } from 'react';
import Papa from 'papaparse';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiUploadCloud, FiCheckCircle, FiBarChart2, FiFileText, FiInfo } = FiIcons;

const CSVImporter = ({ existingParts, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => setData(results.data),
        error: (err) => alert("Error parsing CSV: " + err.message)
      });
    }
  };

  /**
   * Validation logic updated to use OEM_PN as duplicate detection key
   */
  const validation = useMemo(() => {
    // Existing set of OEM PNs
    const existingOEMs = new Set(existingParts.map(p => (p.oem_pn || '').toLowerCase().trim()));
    
    return data.map(row => {
      // Flexibly check for OEM_PN header
      const oemPN = (row.OEM_PN || row.oem_pn || '').trim();
      const internalPN = (row.PART_NUMBR || row.part_numbr || '').trim();
      
      const isDuplicate = existingOEMs.has(oemPN.toLowerCase()) && oemPN !== '';
      const isValid = !!oemPN || !!internalPN;

      return {
        row,
        clean_oem: oemPN,
        clean_internal: internalPN,
        isValid,
        isDuplicate,
        errors: [
          (!oemPN && !internalPN) && "Missing Part Number",
          isDuplicate && "Existing OEM Record"
        ].filter(Boolean)
      };
    });
  }, [data, existingParts]);

  const stats = useMemo(() => {
    const valid = validation.filter(v => v.isValid && !v.isDuplicate).length;
    const duplicates = validation.filter(v => v.isDuplicate).length;
    const invalid = validation.filter(v => !v.isValid).length;
    return { total: data.length, valid, invalid, duplicates };
  }, [validation, data]);

  const handleImport = async () => {
    setIsProcessing(true);
    
    const toImport = validation
      .filter(v => v.isValid && !v.isDuplicate)
      .map(v => {
        const payload = {};
        Object.keys(v.row).forEach(key => {
          payload[key.toLowerCase()] = v.row[key] || '';
        });
        // Ensure status defaults to Active
        payload.status = 'Active';
        return payload;
      });

    if (toImport.length === 0) {
      setIsProcessing(false);
      return;
    }

    const batchSize = 500;
    let successCount = 0;

    for (let i = 0; i < toImport.length; i += batchSize) {
      const batch = toImport.slice(i, i + batchSize);
      try {
        const { imported } = await api('importParts', { rows: batch });
        successCount += imported;
      } catch (err) {
        alert(`Database error: ${err.message}`);
        setIsProcessing(false);
        return;
      }
    }

    setImportResults({
      total: stats.total,
      imported: successCount,
      invalid: stats.invalid,
      duplicates: stats.duplicates
    });
    setIsProcessing(false);
  };

  if (importResults) return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 dark:bg-slate-950/95 backdrop-blur-xl">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-md p-8 text-center shadow-2xl">
        <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiBarChart2} className="w-10 h-10 text-emerald-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Import Complete</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">Processed {importResults.total.toLocaleString()} rows.</p>
        
        <div className="space-y-4 mb-8">
          <div className="flex justify-between p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-2xl">
            <span className="text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest text-xs">Newly Loaded</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-black text-xl">{importResults.imported.toLocaleString()}</span>
          </div>
          <div className="flex justify-between p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-transparent rounded-2xl">
            <span className="text-slate-500 font-bold uppercase tracking-widest text-xs">Skipped (Existing)</span>
            <span className="text-slate-900 dark:text-white font-black text-xl">{importResults.duplicates.toLocaleString()}</span>
          </div>
        </div>

        <button 
          onClick={() => {
            onSuccess(`Imported ${importResults.imported} records`);
            onClose();
          }}
          className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl transition-all shadow-lg shadow-blue-900/20"
        >
          Finish & Refresh
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 dark:bg-slate-950/90 backdrop-blur-md">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <SafeIcon icon={FiUploadCloud} className="text-blue-600 dark:text-blue-500" />
              Bulk CSV Importer
            </h2>
            <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest font-black">Duplicate Check Column: "OEM_PN"</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!file ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="h-72 border-2 border-dashed border-slate-300 dark:border-slate-800 rounded-3xl flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950/30 hover:border-blue-500 hover:bg-blue-50 dark:hover:border-blue-500/5 transition-all cursor-pointer group"
            >
              <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileChange} className="hidden" />
              <div className="w-20 h-20 bg-white dark:bg-slate-800 border border-slate-200 dark:border-transparent group-hover:bg-blue-600 group-hover:border-blue-600 rounded-2xl flex items-center justify-center mb-6 transition-colors shadow-sm dark:shadow-2xl">
                <SafeIcon icon={FiUploadCloud} className="w-10 h-10 text-slate-400 group-hover:text-white" />
              </div>
              <span className="text-xl font-bold text-slate-700 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Select Master Inventory CSV</span>
              <p className="text-xs text-slate-500 mt-2 font-bold uppercase tracking-widest">Requirement: OEM_PN or PART_NUMBR Column required</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-500/10 p-2 rounded-lg"><SafeIcon icon={FiFileText} className="text-blue-600 dark:text-blue-500 w-5 h-5" /></div>
                  <div>
                    <div className="text-sm font-bold text-slate-900 dark:text-white">{file.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Total File Rows: {data.length.toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={() => setFile(null)} className="text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-3 py-1 bg-red-50 dark:bg-red-400/10 rounded-lg">Reset</button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <StatCard label="Ready to Load" val={stats.valid} color="emerald" icon={FiCheckCircle} />
                <StatCard label="Existing (Skip)" val={stats.duplicates} color="slate" icon={FiBarChart2} />
                <StatCard label="Invalid" val={stats.invalid} color="red" icon={FiX} />
              </div>

              <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-slate-50 dark:bg-slate-900 sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-5 py-4 font-black text-slate-500 uppercase">Status</th>
                        <th className="px-5 py-4 font-black text-slate-500 uppercase">OEM_PN</th>
                        <th className="px-5 py-4 font-black text-slate-500 uppercase">Description</th>
                        <th className="px-5 py-4 font-black text-slate-500 uppercase">Brand</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 text-left">
                      {validation.slice(0, 50).map((v, i) => (
                        <tr key={i} className={`${!v.isValid ? 'bg-red-50 dark:bg-red-500/5' : v.isDuplicate ? 'bg-slate-50 dark:bg-slate-800/20' : ''}`}>
                          <td className="px-5 py-3">
                            <SafeIcon icon={!v.isValid ? FiX : v.isDuplicate ? FiBarChart2 : FiCheckCircle} className={!v.isValid ? 'text-red-500' : v.isDuplicate ? 'text-slate-400' : 'text-emerald-500'} />
                          </td>
                          <td className="px-5 py-3 font-mono font-bold text-slate-900 dark:text-white">{v.clean_oem || v.clean_internal || 'MISSING'}</td>
                          <td className="px-5 py-3 text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{v.row.DESCRIP1 || 'N/A'}</td>
                          <td className="px-5 py-3 text-slate-500">{v.row.APPL_BRAND || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {validation.length > 50 && (
                    <div className="p-4 text-center text-slate-500 font-bold uppercase text-[10px] tracking-widest border-t border-slate-100 dark:border-slate-800">
                      Previewing first 50 rows of {validation.length.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
          <div className="flex items-center gap-2 text-slate-500 text-[10px] font-black uppercase tracking-widest">
            <SafeIcon icon={FiInfo} className="text-blue-500 w-4 h-4" />
            OEM PN Duplicates are auto-skipped
          </div>
          <div className="flex gap-4">
            <button onClick={onClose} className="px-6 py-2.5 text-slate-600 dark:text-slate-400 font-bold">Cancel</button>
            <button 
              onClick={handleImport}
              disabled={!stats.valid || isProcessing}
              className="px-10 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-300 dark:disabled:bg-slate-800 text-white font-black rounded-xl transition-all shadow-md dark:shadow-blue-900/20 flex items-center gap-3"
            >
              {isProcessing ? (
                <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> PROCESSING...</>
              ) : (
                <>CONFIRM BULK LOAD ({stats.valid.toLocaleString()})</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, val, color, icon }) => {
  const themes = {
    slate: 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-500',
    red: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-500'
  };
  return (
    <div className={`p-4 rounded-2xl border ${themes[color]} flex flex-col`}>
      <span className="text-[10px] uppercase font-black tracking-widest opacity-70">{label}</span>
      <div className="flex items-center justify-between mt-1">
        <span className="text-2xl font-black">{val.toLocaleString()}</span>
        {icon && <SafeIcon icon={icon} className="w-5 h-5 opacity-50" />}
      </div>
    </div>
  );
};

export default CSVImporter;